export function formatDate(value: string | null | undefined): string {
  if (!value) return '日期未维护';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

export function formatCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.join('、') || '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * 状态码 → 中文。
 *
 * 存在的理由：交付物列表一屏就能数出 240 多个 `pending` / `not_submitted`
 * 直接怼在用户脸上。工程师不认识这些英文码，也不该被要求认识。
 *
 * 覆盖计划节点、交付物、审批、问题、变更几套状态——它们在同一个列表里混着出现，
 * 分开维护必然漏掉某一套。
 */
const STATUS_TEXT: Readonly<Record<string, string>> = {
  // 计划节点 / 通用
  pending: '待处理',
  in_progress: '进行中',
  blocked: '受阻',
  completed: '已完成',
  not_applicable: '不适用',
  cancelled: '已取消',
  // 审批
  not_submitted: '未提交',
  submitted: '已提交',
  in_review: '审批中',
  approved: '已批准',
  rejected: '已退回',
  // 记录/文档
  draft: '草稿',
  released: '已发布',
  legacy_orphan: '待归属',
  published: '已发布',
  closed: '已关闭',
  active: '进行中',
  open: '未关闭',
  // 时效
  overdue: '已逾期',
  'due-soon': '即将到期',
  normal: '正常',
  /*
    0916 Tony:「项目状态标识不清楚, 只有灰色英文字母体现」。

    模块中心的项目列表上，11 个 deleted、1 个 archived 就这么以**英文原样**
    显示着——`statusText` 认识就翻、不认识原样返回，而这三个词一直没进表。
    库里还有 13 张 superseded 的审批单同样漏网。
    补在这里而不是在那张表上现翻：翻译只能有一处，各页各翻必然分叉。
  */
  deleted: '已删除',
  archived: '已归档',
  superseded: '已作废',
  quotation: '报价',
  paused: '已暂停',
  // 1027：看板 TERMINAL 早就认这两个终态码，这张表却一直没收，列表上露英文
  inactive: '停用',
  obsolete: '已作废',
};

/**
 * 优先级。规范值是 critical/high/normal/low（模块定义里的选项、DTO 校验都用它）。
 *
 * 0916 之前 `TaskDetailDrawer` 和 `TaskTrackerTable` 各写了一份一模一样的表，
 * 模块中心的看板干脆不翻、直接把 normal/high 画在图上——
 * 和 Tony 说的「只有灰色英文字母体现」是同一个毛病，只是换了个字段。
 * 中文旧值也认：库里 0916 才统一，别的表可能还留着。
 */
const PRIORITY_TEXT: Readonly<Record<string, string>> = {
  critical: '紧急', high: '高', normal: '普通', low: '低',
  紧急: '紧急', 高: '高', 中: '普通', 低: '低',
};

export function priorityText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  const key = String(value).trim();
  return PRIORITY_TEXT[key] ?? PRIORITY_TEXT[key.toLowerCase()] ?? key;
}

/** 认识就翻译，不认识就原样返回——绝不编一个看起来像中文的假标签 */
export function statusText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  const key = String(value).trim();
  return STATUS_TEXT[key] ?? STATUS_TEXT[key.toLowerCase()] ?? key;
}

export function formatResponsibility(
  value: unknown,
  fallback = '责任人未分配',
): string {
  if (value === null || value === undefined) return fallback;
  if (Array.isArray(value)) {
    const names = value
      .filter((name): name is string => typeof name === 'string')
      .map((name) => name.trim())
      .filter(Boolean);
    return names.length ? names.join('、') : fallback;
  }
  if (typeof value !== 'string') return String(value);

  const candidate = value.trim();
  if (!candidate) return fallback;
  if (candidate.startsWith('[') && candidate.endsWith(']')) {
    try {
      const parsed: unknown = JSON.parse(candidate);
      if (Array.isArray(parsed) && parsed.every((name) => typeof name === 'string')) {
        const names = parsed.map((name) => name.trim()).filter(Boolean);
        return names.length ? names.join('、') : fallback;
      }
    } catch {
      // Preserve malformed legacy text as-is.
    }
  }
  return value;
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
